import type {
  DonationFundingConfig,
  DonationFundingMethod,
  HostedPaymentSessionInput,
} from '@triedge/domain-core';
import {
  PaymentReturnNotice,
  PaymentStatusPill,
  SOLA_IFIELDS_FRAME_SRC,
  configureSolaIFields,
  readHostedCheckoutReturnState,
  tokenizeSolaIFields,
} from '@triedge/payments';
import { HeartIcon } from 'lucide-react';
import type { ChangeEvent, FormEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { SupportErrorMessage } from '../../../components/overlays/EngineeringSupportBanner';
import {
  useGetDonationsCampaignCalendarQuery,
  useListDonationsFamilyHistoryQuery,
} from '../../../state/domainRouterApi';
import { resolveCognitoProfile } from '../../../utils/cognito-profile';
import {
  createDonationDafRecommendation,
  createHostedPaymentSession,
  getDonationFundingConfig,
} from '../../../utils/payments';

const normalizeText = (value: string | null | undefined): string =>
  String(value ?? '').replace(/\s+/g, ' ').trim();

const parseCurrencyToDollars = (value: string | null | undefined): number | null => {
  const normalized = normalizeText(value).replace(/[^0-9.]+/g, '');
  if (!normalized) return null;
  const amount = Number.parseFloat(normalized);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return amount;
};

const resolveCheckoutUrl = (kind: 'success' | 'cancel'): string => {
  const url = new URL(window.location.href);
  url.searchParams.set('checkout', kind);
  return url.toString();
};

const resolvePaymentProfile = () => resolveCognitoProfile();

const extractPaymentErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }
  if (error && typeof error === 'object') {
    const message = 'message' in error ? String((error as { message?: unknown }).message ?? '').trim() : '';
    if (message) {
      return message;
    }
    if (Array.isArray((error as { errors?: Array<{ message?: unknown }> }).errors)) {
      const nested = (error as { errors?: Array<{ message?: unknown }> }).errors
        ?.map((entry) => String(entry?.message ?? '').trim())
        .filter(Boolean)
        .join('; ');
      if (nested) {
        return nested;
      }
    }
  }
  return String(error ?? '').trim() || 'Unknown payment error';
};

const SUGGESTED_AMOUNTS = [36, 72, 180, 360];

const formatCurrency = (cents: number): string =>
  `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const getFundingMethodAvailability = (
  config: DonationFundingConfig | null,
  method: DonationFundingMethod,
) => config?.availableFundingMethods.find((entry) => entry.method === method) ?? null;

function CampaignSummary({
  campaignName,
  priceCents,
  startDate,
  endDate,
  totalRaisedCents,
  donationCount,
}: {
  campaignName: string;
  priceCents: number;
  startDate: string;
  endDate: string;
  totalRaisedCents: number;
  donationCount: number;
}) {
  return (
    <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-center gap-3">
        <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.05em] text-primary">
          Active campaign
        </span>
        <span className="text-sm text-text-secondary">{startDate} to {endDate}</span>
      </div>
      <h2 className="mt-4 font-heading text-[28px] font-semibold text-iron-grey">{campaignName}</h2>
      <p className="mt-2 text-sm text-text-secondary">
        Suggested day-of-impact gift: {formatCurrency(priceCents)}. Family giving history below is sourced from the persisted donations ledger.
      </p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-border bg-surface-light p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">Your total giving</p>
          <p className="mt-2 text-[28px] font-semibold text-iron-grey">{formatCurrency(totalRaisedCents)}</p>
        </div>
        <div className="rounded-lg border border-border bg-surface-light p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.05em] text-text-secondary">Recorded gifts</p>
          <p className="mt-2 text-[28px] font-semibold text-iron-grey">{donationCount}</p>
        </div>
      </div>
    </div>
  );
}

function FamilyGivingHistory({
  items,
}: {
  items: Array<{
    id: string;
    campaignName: string;
    amountCents: number;
    status: string;
    createdAt: number;
  }>;
}) {
  return (
    <div className="rounded-xl border border-border bg-white p-6 shadow-sm">
      <div className="mb-6 flex items-center gap-2">
        <HeartIcon className="h-5 w-5 text-primary" aria-hidden="true" />
        <h2 className="font-heading text-lg font-semibold text-iron-grey">Your Giving History</h2>
      </div>
      {items.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-border">
          <div className="source-faithful-tableScroll">
            <table className="min-w-[520px] w-full">
              <thead className="bg-muted">
                <tr className="text-left text-[12px] uppercase tracking-[0.05em] text-text-secondary">
                  {['Campaign', 'Amount', 'Date', 'Status'].map((label) => (
                    <th key={label} className="px-4 py-3 font-normal">{label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-t border-border">
                    <td className="px-4 py-4 text-sm font-medium text-iron-grey">{item.campaignName}</td>
                    <td className="px-4 py-4 text-sm text-iron-grey">{formatCurrency(item.amountCents)}</td>
                    <td className="px-4 py-4 text-sm text-text-secondary">
                      {new Date(item.createdAt * 1000).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </td>
                    <td className="px-4 py-4 text-sm text-iron-grey">
                      <PaymentStatusPill status={item.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <p className="text-sm text-text-secondary">
          No prior donations are recorded for this family yet.
        </p>
      )}
    </div>
  );
}

function DonationForm({
  campaignId,
  campaignName,
  campaignType,
  onDonationUpdated,
}: {
  campaignId: string;
  campaignName: string;
  campaignType: string;
  onDonationUpdated: () => void;
}) {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(72);
  const [customAmount, setCustomAmount] = useState('');
  const [fundingConfig, setFundingConfig] = useState<DonationFundingConfig | null>(null);
  const [fundingConfigLoading, setFundingConfigLoading] = useState(true);
  const [fundingConfigError, setFundingConfigError] = useState<string | null>(null);
  const [fundingMethod, setFundingMethod] = useState<DonationFundingMethod>('sola_daf');
  const [frequency, setFrequency] = useState<'one-time' | 'monthly'>('one-time');
  const [showDedication, setShowDedication] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [cardExp, setCardExp] = useState('');
  const [recommendationResult, setRecommendationResult] = useState<{
    donationId: string;
    providerTransactionRef: string;
  } | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: resolvePaymentProfile()?.email ?? '',
    phone: '',
    dedication: '',
    displayName: true,
    coverFees: false,
  });
  const solaCardTokenRef = useRef<HTMLInputElement | null>(null);
  const solaCvvTokenRef = useRef<HTMLInputElement | null>(null);
  const solaCardErrorRef = useRef<HTMLLabelElement | null>(null);

  const solaOption = useMemo(
    () => getFundingMethodAvailability(fundingConfig, 'sola_daf'),
    [fundingConfig],
  );
  const stripeOption = useMemo(
    () => getFundingMethodAvailability(fundingConfig, 'stripe_card'),
    [fundingConfig],
  );
  const solaAvailable = Boolean(solaOption?.available);
  const stripeAvailable = stripeOption?.available ?? true;
  const recurringFundingMethod = fundingConfig?.recurring.resolvedFundingMethod ?? null;
  const recurringAvailable = Boolean(recurringFundingMethod) || stripeAvailable;

  useEffect(() => {
    let cancelled = false;
    const loadFundingConfig = async () => {
      setFundingConfigLoading(true);
      try {
        const config = await getDonationFundingConfig();
        if (cancelled) {
          return;
        }
        setFundingConfig(config);
        setFundingConfigError(null);
      } catch (error) {
        if (cancelled) {
          return;
        }
        setFundingConfig(null);
        setFundingConfigError(
          `Sola donation funding options could not be loaded. Stripe card fallback remains available. ${extractPaymentErrorMessage(error)}`,
        );
      } finally {
        if (!cancelled) {
          setFundingConfigLoading(false);
        }
      }
    };
    void loadFundingConfig();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!fundingConfig) {
      return;
    }
    if (frequency === 'monthly') {
      setFundingMethod(recurringFundingMethod ?? 'stripe_card');
      return;
    }

    if (fundingConfig.oneTime.resolvedFundingMethod) {
      setFundingMethod(fundingConfig.oneTime.resolvedFundingMethod);
      return;
    }

    const fallback = getFundingMethodAvailability(
      fundingConfig,
      fundingConfig.oneTime.fallbackFundingMethod ?? fundingConfig.fallbackFundingMethod,
    );
    if (fallback?.available) {
      setFundingMethod(fallback.method);
      return;
    }

    setFundingMethod('stripe_card');
  }, [frequency, fundingConfig, recurringFundingMethod]);

  useEffect(() => {
    if (!fundingConfig) {
      if (!fundingConfigLoading) {
        setFundingMethod('stripe_card');
      }
      return;
    }

    if (fundingMethod === 'sola_daf' && !solaAvailable) {
      setFundingMethod(
        frequency === 'monthly'
          ? recurringFundingMethod ?? 'stripe_card'
          : fundingConfig.oneTime.resolvedFundingMethod ?? 'stripe_card',
      );
    }
  }, [
    fundingConfig,
    fundingConfigLoading,
    fundingMethod,
    frequency,
    recurringFundingMethod,
    solaAvailable,
  ]);

  useEffect(() => {
    if (fundingMethod !== 'sola_daf' || !fundingConfig?.sola.publicIfieldsKey) {
      return;
    }
    let cancelled = false;
    const configureIFields = async () => {
      try {
        await configureSolaIFields({
          publicIfieldsKey: fundingConfig.sola.publicIfieldsKey!,
          softwareName: 'Triedge Flow',
          softwareVersion: '2026.04',
        });
        if (cancelled) {
          return;
        }
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(extractPaymentErrorMessage(error));
        }
      }
    };
    void configureIFields();
    return () => {
      cancelled = true;
    };
  }, [fundingConfig?.sola.publicIfieldsKey, fundingMethod]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const submitStripeDonation = async (
    amountCents: number,
    feeCents: number,
  ) => {
    const profile = resolvePaymentProfile();
    const lineItems: HostedPaymentSessionInput['lineItems'] = [
      {
        name: campaignName,
        description: frequency === 'monthly' ? 'Monthly donation' : 'One-time donation',
        quantity: 1,
        unitAmountCents: amountCents,
        ...(frequency === 'monthly' ? { recurringInterval: 'month' } : {}),
      },
    ];

    if (feeCents > 0) {
      lineItems.push({
        name: 'Processing cost coverage',
        description: 'Optional donor fee recovery',
        quantity: 1,
        unitAmountCents: feeCents,
        ...(frequency === 'monthly' ? { recurringInterval: 'month' } : {}),
      });
    }

    const url = await createHostedPaymentSession({
      moduleId: 'donations',
      operationKey: frequency === 'monthly'
        ? 'donations_checkout_recurring'
        : 'donations_checkout_one_time',
      mode: frequency === 'monthly' ? 'subscription' : 'payment',
      currency: 'usd',
      successUrl: resolveCheckoutUrl('success'),
      cancelUrl: resolveCheckoutUrl('cancel'),
      customerEmail: formData.email,
      customerReferenceId: profile?.userSub || formData.email,
      description: 'Parent portal donations checkout',
      lineItems,
      metadata: {
        route: '/parent/portal/donations',
        campaign_id: campaignId,
        campaign_name: campaignName,
        campaign_type: campaignType,
        donorFirstName: formData.firstName,
        donorLastName: formData.lastName,
        donorPhone: formData.phone,
        dedication: formData.dedication,
        recognition: formData.displayName ? 'public' : 'anonymous',
        coverFees: String(formData.coverFees),
      },
    });
    window.location.assign(url);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setErrorMessage(null);
    setRecommendationResult(null);
    try {
      const baseAmount = selectedAmount ?? parseCurrencyToDollars(customAmount)?.valueOf();
      if (!baseAmount) {
        throw new Error('Choose a donation amount before checkout.');
      }
      const amountCents = Math.round(baseAmount * 100);
      const feeCents = formData.coverFees ? Math.max(Math.round(amountCents * 0.03), 1) : 0;
      if (fundingMethod === 'sola_daf') {
        if (frequency !== 'one-time') {
          throw new Error('Recurring donations currently require the Stripe card fallback.');
        }
        if (!/^\d{4}$/.test(cardExp)) {
          throw new Error('Enter the card expiration as MMYY before submitting a DAF recommendation.');
        }
        const { cardToken, cvvToken } = await tokenizeSolaIFields({
          cardTokenInput: solaCardTokenRef.current,
          cvvTokenInput: solaCvvTokenRef.current,
          errorLabel: solaCardErrorRef.current,
        });
        const result = await createDonationDafRecommendation({
          campaignId,
          grossCents: amountCents,
          feeRecoveryCents: feeCents,
          exp: cardExp,
          cardToken,
          cvvToken,
          donorFirstName: formData.firstName,
          donorLastName: formData.lastName,
          donorEmail: formData.email,
          donorPhone: formData.phone || null,
          dedicationType: formData.dedication ? 'HONOR' : null,
          honoreeName: formData.dedication || null,
          dedicationMessage: formData.dedication || null,
          isAnonymous: !formData.displayName,
          publicDisplayName: formData.displayName
            ? `${formData.firstName} ${formData.lastName}`.trim()
            : null,
          route: '/parent/portal/donations',
        });
        setRecommendationResult({
          donationId: result.donationId,
          providerTransactionRef: result.providerTransactionRef,
        });
        onDonationUpdated();
        setSubmitting(false);
        return;
      }

      await submitStripeDonation(amountCents, feeCents);
    } catch (error) {
      setErrorMessage(extractPaymentErrorMessage(error));
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="parent-donations-payment-policy"
      data-daf-recommendation-scope="one-time-donations-only"
      data-stripe-operation-key-one-time="donations_checkout_one_time"
      data-stripe-operation-key-recurring="donations_checkout_recurring"
      className="rounded-xl border border-border bg-white p-6 shadow-sm md:p-8"
    >
      <h2 className="mb-6 font-heading text-xl font-semibold text-iron-grey">Make a Gift</h2>

      <div className="mb-6 rounded-xl border border-border bg-surface-light p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">
              Donation Funding
            </p>
            <p className="mt-1 text-sm text-text-secondary">
              Sola DAF is the primary path when configured. Stripe card checkout remains the live backup.
            </p>
          </div>
          {fundingConfigLoading ? (
            <span className="text-xs text-text-secondary">Loading…</span>
          ) : null}
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {([
            {
              method: 'sola_daf' as const,
              title: 'Sola DAF',
              detail: 'Submit a donor-advised fund recommendation directly through Sola.',
              available: solaAvailable,
              reason: solaOption?.reasonMessage ?? 'Unavailable until the Sola donation lane is configured.',
            },
            {
              method: 'stripe_card' as const,
              title: 'Stripe Card Backup',
              detail: 'Use hosted Stripe checkout when DAF is unavailable or recurring giving is required.',
              available: stripeAvailable,
              reason: stripeOption?.reasonMessage ?? 'Stripe fallback is unavailable until the Stripe control plane is active.',
            },
          ]).map((option) => (
            <button
              key={option.method}
              type="button"
              onClick={() => option.available && setFundingMethod(option.method)}
              disabled={!option.available}
              className={`rounded-xl border px-4 py-4 text-left transition-colors ${
                fundingMethod === option.method
                  ? 'border-primary bg-primary/5'
                  : 'border-border bg-white'
              } ${option.available ? 'hover:border-primary' : 'cursor-not-allowed opacity-60'}`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold text-iron-grey">{option.title}</span>
                {option.available ? (
                  <span className="rounded-full bg-success-background px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-jungle">
                    Ready
                  </span>
                ) : (
                  <span className="rounded-full bg-muted px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-secondary">
                    Unavailable
                  </span>
                )}
              </div>
              <p className="mt-2 text-sm text-text-secondary">
                {option.available ? option.detail : option.reason}
              </p>
            </button>
          ))}
        </div>
        {fundingConfigError ? (
          <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {fundingConfigError}
          </p>
        ) : null}
      </div>

      <div className="mb-6 flex rounded-lg bg-muted p-1" role="radiogroup" aria-label="Donation frequency">
        {(['one-time', 'monthly'] as const).map((option) => (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={frequency === option}
            onClick={() => {
              setFrequency(option);
            }}
            disabled={option === 'monthly' && !recurringAvailable}
            className={`flex-1 rounded-md py-2 text-sm font-medium transition-colors ${
              frequency === option
                ? 'bg-primary text-white shadow-sm'
                : 'text-iron-grey hover:bg-gray-200'
            }`}
          >
            {option === 'one-time' ? 'One-Time' : 'Monthly'}
          </button>
        ))}
      </div>
      {fundingMethod === 'sola_daf' ? (
        <p className="mb-6 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-iron-grey">
          DAF recommendations currently support one-time gifts only. Switch to the Stripe backup for recurring giving.
        </p>
      ) : null}

      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {SUGGESTED_AMOUNTS.map((amount) => (
          <button
            key={amount}
            type="button"
            onClick={() => {
              setSelectedAmount(amount);
              setCustomAmount('');
            }}
            aria-pressed={selectedAmount === amount}
            className={`rounded-lg border-2 py-3 text-center font-semibold transition-colors ${
              selectedAmount === amount
                ? 'border-primary bg-primary text-white'
                : 'border-border text-iron-grey hover:border-primary'
            }`}
          >
            ${amount}
          </button>
        ))}
      </div>

      <div className="mb-8">
        <label htmlFor="customAmount" className="mb-1 block text-sm font-medium text-iron-grey">
          Other amount
        </label>
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <span className="text-sm text-text-secondary">$</span>
          </div>
          <input
            id="customAmount"
            name="customAmount"
            type="number"
            min="1"
            step="1"
            value={customAmount}
            onChange={(event) => {
              setCustomAmount(event.target.value);
              setSelectedAmount(null);
            }}
            className="block w-full rounded-md border border-border py-2.5 pl-7 pr-3 text-sm text-iron-grey focus:border-primary focus:ring-2 focus:ring-primary/20"
            placeholder="Enter amount"
          />
        </div>
      </div>

      <div className="mb-6 space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {[
            { id: 'firstName', label: 'First Name', required: true },
            { id: 'lastName', label: 'Last Name', required: true },
          ].map((field) => (
            <div key={field.id}>
              <label htmlFor={field.id} className="mb-1 block text-sm font-medium text-iron-grey">
                {field.label}
                {field.required ? <span className="ml-1 text-strawberry">*</span> : null}
              </label>
              <input
                id={field.id}
                name={field.id}
                required={field.required}
                value={formData[field.id as 'firstName' | 'lastName']}
                onChange={handleInputChange}
                className="block w-full rounded-md border border-border px-3 py-2.5 text-sm text-iron-grey focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>
          ))}
        </div>

        <div>
          <label htmlFor="email" className="mb-1 block text-sm font-medium text-iron-grey">
            Email<span className="ml-1 text-strawberry">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={handleInputChange}
            className="block w-full rounded-md border border-border px-3 py-2.5 text-sm text-iron-grey focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
        </div>

        <div>
          <label htmlFor="phone" className="mb-1 block text-sm font-medium text-iron-grey">
            Phone <span className="font-normal text-text-tertiary">(Optional)</span>
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleInputChange}
            className="block w-full rounded-md border border-border px-3 py-2.5 text-sm text-iron-grey focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
        </div>
      </div>

      <div className="mb-6 overflow-hidden rounded-lg border border-border">
        <button
          type="button"
          onClick={() => setShowDedication((open) => !open)}
          className="flex w-full items-center justify-between bg-muted/50 px-4 py-3 text-left transition-colors hover:bg-muted"
          aria-expanded={showDedication}
          aria-controls="dedication-content"
        >
          <div>
            <span className="block text-sm font-medium text-iron-grey">Add a Dedication</span>
            <span className="mt-0.5 block text-xs text-text-secondary">In Honor Of / In Memory Of</span>
          </div>
          <span className="text-text-secondary">{showDedication ? '−' : '+'}</span>
        </button>
        {showDedication ? (
          <div id="dedication-content" className="border-t border-border bg-white p-4">
            <input
              id="dedication"
              name="dedication"
              type="text"
              value={formData.dedication}
              onChange={handleInputChange}
              placeholder="E.g., In memory of..."
              className="block w-full rounded-md border border-border px-3 py-2.5 text-sm text-iron-grey focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>
        ) : null}
      </div>

      <div className="mb-8 space-y-3 text-sm">
        <label className="flex items-start gap-3">
          <input
            id="displayName"
            name="displayName"
            type="checkbox"
            checked={formData.displayName}
            onChange={handleInputChange}
            className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
          />
          <span className="font-medium text-iron-grey">Display my family name in donor recognition</span>
        </label>

        <label className="flex items-start gap-3">
          <input
            id="coverFees"
            name="coverFees"
            type="checkbox"
            checked={formData.coverFees}
            onChange={handleInputChange}
            className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
          />
          <span className="font-medium text-iron-grey">Help cover processing costs</span>
        </label>
      </div>

      {fundingMethod === 'sola_daf' ? (
        <div className="mb-8 rounded-xl border border-border bg-surface-light p-4">
          <p className="text-sm font-semibold text-iron-grey">DAF Card Details</p>
          <p className="mt-1 text-sm text-text-secondary">
            Card details are tokenized through Sola iFields. Your browser never submits raw card data to Triedge.
          </p>
          <div className="mt-4 grid gap-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-iron-grey">
                Card Number<span className="ml-1 text-strawberry">*</span>
              </label>
              <iframe
                title="Sola DAF card number"
                data-ifields-id="card-number"
                data-ifields-placeholder="Card Number"
                src={SOLA_IFIELDS_FRAME_SRC}
                className="block h-11 w-full rounded-md border border-border bg-white px-3 py-2.5"
              />
              <input
                ref={solaCardTokenRef}
                name="xCardNum"
                data-ifields-id="card-number-token"
                type="hidden"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-iron-grey">
                  CVV<span className="ml-1 text-strawberry">*</span>
                </label>
                <iframe
                  title="Sola DAF CVV"
                  data-ifields-id="cvv"
                  data-ifields-placeholder="CVV"
                  src={SOLA_IFIELDS_FRAME_SRC}
                  className="block h-11 w-full rounded-md border border-border bg-white px-3 py-2.5"
                />
                <input
                  ref={solaCvvTokenRef}
                  name="xCVV"
                  data-ifields-id="cvv-token"
                  type="hidden"
                />
              </div>
              <div>
                <label htmlFor="cardExp" className="mb-1 block text-sm font-medium text-iron-grey">
                  Expiration (MMYY)<span className="ml-1 text-strawberry">*</span>
                </label>
                <input
                  id="cardExp"
                  name="cardExp"
                  inputMode="numeric"
                  pattern="[0-9]{4}"
                  maxLength={4}
                  value={cardExp}
                  onChange={(event) => setCardExp(event.currentTarget.value.replace(/\D+/g, '').slice(0, 4))}
                  className="block w-full rounded-md border border-border bg-white px-3 py-2.5 text-sm text-iron-grey focus:border-primary focus:ring-2 focus:ring-primary/20"
                  placeholder="MMYY"
                />
              </div>
            </div>
            <label
              ref={solaCardErrorRef}
              data-ifields-id="card-data-error"
              className="text-sm text-strawberry"
            />
          </div>
        </div>
      ) : null}

      {errorMessage ? (
        <SupportErrorMessage className="mb-6">
          {errorMessage}
        </SupportErrorMessage>
      ) : null}
      {recommendationResult ? (
        <div className="mb-6 rounded-lg border border-[#BBF7D0] bg-[#F0FDF4] px-4 py-4 text-sm text-[#166534]">
          <p className="font-semibold">DAF recommendation submitted</p>
          <p className="mt-1">
            Your recommendation has been submitted through Sola. Donation history will show it as pending until it is reconciled.
          </p>
          <p className="mt-2 font-mono text-xs text-[#166534]">
            Ref {recommendationResult.providerTransactionRef} · Donation {recommendationResult.donationId}
          </p>
        </div>
      ) : null}

      <button
        type="submit"
        disabled={submitting}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 font-semibold text-white transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
      >
        <HeartIcon className="h-5 w-5" aria-hidden="true" />
        {submitting
          ? fundingMethod === 'sola_daf'
            ? 'Submitting…'
            : 'Redirecting…'
          : fundingMethod === 'sola_daf'
            ? 'Submit DAF Recommendation'
            : 'Complete Donation'}
      </button>
    </form>
  );
}

function ParentDonationsPage() {
  const location = useLocation();
  const campaignQuery = useGetDonationsCampaignCalendarQuery({ campaignType: 'annual-fund' });
  const historyQuery = useListDonationsFamilyHistoryQuery({ limit: 25, campaignType: 'annual-fund' });

  const familyHistory = useMemo(() => {
    const items = Array.isArray(historyQuery.data?.items) ? historyQuery.data.items : [];
    return items.map((item) => ({
      id: item.id,
      campaignName: item.campaignName,
      amountCents: Number(item.grossCents ?? 0) + Number(item.feeRecoveryCents ?? 0),
      status: item.status,
      createdAt: Number(item.createdAt ?? 0),
    }));
  }, [historyQuery.data?.items]);

  const totalRaisedCents = familyHistory.reduce((sum, item) => sum + item.amountCents, 0);
  const campaignName = campaignQuery.data?.campaignName ?? 'Current donation campaign';
  const campaignId = campaignQuery.data?.campaignId ?? '';
  const campaignType = campaignQuery.data?.campaignType ?? 'general';
  const hasExpectedCampaignType = campaignType === 'annual-fund';
  const priceCents = Number(campaignQuery.data?.priceCents ?? 0);
  const startDate = campaignQuery.data?.startDate ?? '--';
  const endDate = campaignQuery.data?.endDate ?? '--';
  const hostedReturnState = useMemo(
    () => readHostedCheckoutReturnState(location.search),
    [location.search],
  );

  return (
    <div
      data-testid="parent-donations-source-faithful-root"
      data-extracted-from="source:parent-donations-v1.0"
      data-donation-campaign-type={campaignType}
      data-donation-campaign-id={campaignId || undefined}
      data-daf-recommendation-scope="one-time-donations-only"
      className="min-h-screen bg-background-light font-body"
    >
      <header className="border-b border-border bg-white">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-3 px-4">
          <div className="h-10 w-10 flex-shrink-0 rounded-md bg-primary" aria-hidden="true" />
          <span className="font-heading text-lg font-semibold text-iron-grey">Yeshiva Shaarei Tzion</span>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-8 px-4 py-8 md:py-12">
        <PaymentReturnNotice
          state={hostedReturnState}
          noticeKey="parent-donations"
          successTitle="Donation handoff completed"
          successBody="Stripe returned successfully. Your giving history will update as the donation record is reconciled."
          cancelTitle="Donation checkout was cancelled"
          cancelBody="No donation was finalized. You can review your giving history or restart checkout when ready."
          guidanceBody="Giving history is the authoritative record after reconciliation. If the new donation is not visible right away, reopen this page after the payment event settles."
          primaryAction={
            <a
              href="#family-giving-history"
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-iron-grey shadow-sm"
            >
              View Giving History
            </a>
          }
        />
        {campaignQuery.isLoading ? (
          <div className="h-48 animate-pulse rounded-xl bg-muted" />
        ) : campaignQuery.isError || !hasExpectedCampaignType ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-6 py-5 text-sm text-amber-800">
            The annual fund donations campaign could not be loaded from persisted data.
          </div>
        ) : (
          <CampaignSummary
            campaignName={campaignName}
            priceCents={priceCents}
            startDate={startDate}
            endDate={endDate}
            totalRaisedCents={totalRaisedCents}
            donationCount={familyHistory.length}
          />
        )}

        <section className="grid grid-cols-1 items-start gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <DonationForm
              campaignId={campaignId}
              campaignName={campaignName}
              campaignType={campaignType}
              onDonationUpdated={() => {
                void historyQuery.refetch();
              }}
            />
          </div>
          <div className="lg:col-span-1">
            {historyQuery.isLoading ? (
              <div className="h-64 animate-pulse rounded-xl bg-muted" />
            ) : historyQuery.isError ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-6 py-5 text-sm text-amber-800">
                Family donation history is temporarily unavailable.
              </div>
            ) : (
              <div id="family-giving-history">
                <FamilyGivingHistory items={familyHistory} />
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

export function ParentDonationsSourceFaithfulRoutes() {
  return (
    <Routes>
      <Route index element={<ParentDonationsPage />} />
      <Route path="*" element={<Navigate to="." replace />} />
    </Routes>
  );
}
